import type { CSSProperties, FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Testimonial } from '@/types'

import { ChartLine, MonitorPlay, Route } from 'lucide-react'
import { getTestimonials, safely } from '@/lib/content'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Container } from '@/components/generals/container'
import { FlowLines } from '@/components/generals/flow-lines'
import { DemoForm } from '@/components/forms/demo-form'

const delay = (ms: number): CSSProperties => ({ '--rise-delay': `${ms}ms` } as CSSProperties)

const EXPECT = [
    {
        title: 'A walkthrough built around your stack',
        body: 'We map Veyra to the banks, accounting system and tools you already use.',
        Icon: MonitorPlay
    },
    {
        title: 'Your forecast, live',
        body: 'See a cash forecast and dashboard modelled on your business, not a generic demo account.',
        Icon: ChartLine
    },
    {
        title: 'A clear onboarding plan',
        body: 'Leave with pricing, a timeline and the exact steps to go live, usually the same week.',
        Icon: Route
    }
]

interface DemoProps {
    testimonial: Testimonial | null
}

const Demo: FC<DemoProps> = ({ testimonial }): ReactNode => {
    return (
        <Layout cta={false}>
            <Seo
                title='Request a demo'
                description='Book a personalized Veyra demo. See your own cash forecast and dashboards, and leave with a clear onboarding plan.'
            />

            <section className='relative overflow-hidden bg-cream'>
                <div aria-hidden='true' className='pointer-events-none absolute inset-0 opacity-60'>
                    <FlowLines variant='waves' />
                </div>

                <Container className='relative pb-24 pt-36 md:pb-32 md:pt-44'>
                    <div className='grid gap-16 lg:grid-cols-12 lg:gap-10'>
                        <div className='lg:col-span-5'>
                            <div className='animate-rise' style={delay(40)}>
                                <Eyebrow>Request a demo</Eyebrow>
                            </div>

                            <h1 className='animate-rise mt-7 font-heading text-display-xl font-medium text-burgundy' style={delay(120)}>
                                See Veyra with <span className='text-stone'>your own numbers.</span>
                            </h1>

                            <p className='animate-rise mt-7 max-w-md text-lg leading-relaxed text-stone-dark' style={delay(220)}>
                                Tell us a little about your team and we will prepare a 30-minute session around your accounts, your reporting and the decisions in front of you.
                            </p>

                            <ul className='animate-rise mt-12 space-y-7' style={delay(300)}>
                                {EXPECT.map(({ title, body, Icon }) => (
                                    <li key={title} className='flex gap-4'>
                                        <span className='flex size-10 shrink-0 items-center justify-center rounded-full bg-burgundy text-gold'>
                                            <Icon aria-hidden='true' className='size-4' />
                                        </span>

                                        <span>
                                            <span className='block font-medium text-burgundy'>{title}</span>
                                            <span className='mt-1 block text-sm leading-relaxed text-stone-dark'>{body}</span>
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            {testimonial && (
                                <figure className='animate-rise mt-14 border-t border-burgundy/10 pt-8' style={delay(380)}>
                                    <blockquote className='font-heading text-xl font-medium leading-snug tracking-[-0.015em] text-burgundy'>
                                        &ldquo;{testimonial.quote}&rdquo;
                                    </blockquote>

                                    <figcaption className='mt-4 text-sm text-stone-dark'>
                                        <span className='font-medium text-burgundy'>{testimonial.name}</span>
                                        {[testimonial.role, testimonial.company].filter(Boolean).length > 0 && (
                                            <span>, {[testimonial.role, testimonial.company].filter(Boolean).join(', ')}</span>
                                        )}
                                    </figcaption>
                                </figure>
                            )}
                        </div>

                        <div className='animate-rise lg:col-span-6 lg:col-start-7' style={delay(240)}>
                            <div className='rounded-[28px] border border-burgundy/10 bg-paper/90 p-6 shadow-[0_50px_100px_-60px_rgb(57_26_24/0.5)] backdrop-blur sm:p-10'>
                                <DemoForm />
                            </div>
                        </div>
                    </div>
                </Container>
            </section>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<DemoProps> = async () => {
    const testimonials = await safely(getTestimonials, [], 'testimonials')

    return {
        props: { testimonial: testimonials[0] ?? null },
        revalidate: 60
    }
}

export default Demo
